import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { usePostHog } from "@finitless/shared/hooks";
import { buildQueryParams } from "../lib/globalUtils";

/**
 * Allowed order types configuration
 * This defines which order types are allowed to be selected in the settings
 * - Keys in this object represent the order type names (for default_order_type)
 * - Values represent the corresponding IDs (for order_types_allowed)
 *
 * To enable more order types, add them to this object
 */
const ALLOWED_ORDER_TYPES = {
  delivery: "1",
  pickup: "2",
  // 'eatin': '3',
  // 'curbside': '4',
  // 'drivethru': '5',
  // 'seatdelivery': '6',
  // 'catering_delivery': '7',
  // 'catering_pickup': '8',
  // 'reservation': '9',
};

// Create arrays for easier filtering
const ALLOWED_DEFAULT_ORDER_TYPE_VALUES = Object.keys(ALLOWED_ORDER_TYPES);
const ALLOWED_ORDER_TYPES_ALLOWED_VALUES = Object.values(ALLOWED_ORDER_TYPES);

const ALLOWED_BASIC_CONFIG_CATEGORIES = [
  "platform_currency_settings",
  "platform_distance_measurement_settings",
  "platform_date_time_formats",
  "platform_fee_tip_settings",
];

const ALLOWED_BASIC_ORDER_TYPES_CONFIG_KEYS = [
  "order_types_allowed",
  "max_distance_pickup",
];

/**
 * Hook to get basic config category (key_basic)
 * @returns {Object} Query result with basic category data
 */
export const useBasicConfigCategory = () => {
  const queryParams = {
    where: [{ attribute: "key", value: "key_basic" }],
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["config-categories", "basic"],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result?.[0] || null;
    },
    enabled: isEndpointEnabled("config-categories", "list"),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get config categories by parent ID
 * @param {number} parentId - Parent category ID
 * @returns {Object} Query result with categories data
 */
export const useConfigCategories = (parentId) => {
  const queryParams = {
    orderBy: "rank",
    where: [{ attribute: "parent_category_id", value: parentId }],
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["config-categories", "by-parent", parentId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || [];
    },
    enabled: isEndpointEnabled("config-categories", "list") && !!parentId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get basic platform category (basic_platform)
 * @returns {Object} Query result with basic platform category
 */
export const useBasicPlatformCategory = () => {
  const { data: basicCategory } = useBasicConfigCategory();
  const { data: platformCategories, ...rest } = useConfigCategories(
    basicCategory?.id,
  );

  // Find the basic_platform category
  const basicPlatformCategory = platformCategories?.find(
    (category) => category.key === "basic_platform",
  );

  return {
    data: basicPlatformCategory,
    ...rest,
  };
};

/**
 * Hook to get platform settings categories (basic_platform children)
 * @returns {Object} Query result with platform categories
 */
export const usePlatformSettingsCategories = () => {
  const { isFeatureEnabled } = usePostHog();

  const {
    data: basicCategory,
    isLoading: basicCategoryLoading,
    error: basicCategoryError,
  } = useBasicConfigCategory();

  const {
    data: platformCategories,
    isLoading: platformCategoriesLoading,
    error: platformCategoriesError,
  } = useConfigCategories(basicCategory?.id);

  const basicPlatformCategory = platformCategories?.find(
    (category) => category.key === "basic_platform",
  );

  const {
    data: platformSettingsCategories,
    isLoading: platformSettingsLoading,
    error: platformSettingsError,
  } = useConfigCategories(basicPlatformCategory?.id);

  const basicOrderTypesCategory = platformCategories?.find(
    (category) => category.key === "basic_order_types",
  );
  const shouldShowDriverTipSettings = isFeatureEnabled(
    "driver_tip_settings_enabled",
  );

  const filteredCategories =
    platformSettingsCategories
      ?.filter((category) => {
        if (!ALLOWED_BASIC_CONFIG_CATEGORIES.includes(category.key)) {
          return false;
        }

        if (
          category.key === "platform_fee_tip_settings" &&
          !shouldShowDriverTipSettings
        ) {
          return false;
        }

        return true;
      })
      .map((category) => {
        // Filter configs based on category
        let allowedConfigKeys = [];

        switch (category.key) {
          case "platform_currency_settings":
            allowedConfigKeys = ["format_number_currency"];
            break;
          case "platform_distance_measurement_settings":
            allowedConfigKeys = ["distance_unit"];
            break;
          case "platform_date_time_formats":
            allowedConfigKeys = ["dates_general_format", "general_hour_format"];
            break;
          case "platform_fee_tip_settings":
            allowedConfigKeys = [
              "driver_tip_type",
              "driver_tip_options",
              "driver_tip_use_custom",
              "platform_fee_fixed",
              "platform_fee_percentage",
            ];
            break;
          default:
            allowedConfigKeys = [];
        }

        // Filter configs within the category
        const filteredConfigs =
          category.configs?.filter((config) =>
            allowedConfigKeys.includes(config.key),
          ) || [];

        return {
          ...category,
          configs: filteredConfigs,
        };
      }) || [];

  const processedOrderTypesConfigs =
    basicOrderTypesCategory?.configs
      ?.filter((config) =>
        ALLOWED_BASIC_ORDER_TYPES_CONFIG_KEYS.includes(config.key),
      )
      .map((config) => {
        let filteredOptions = config.options || [];
        let filteredValue = config.value;

        if (config.key === "default_order_type") {
          // Filter options and value to only show allowed order types
          filteredOptions =
            config.options?.filter((option) =>
              ALLOWED_DEFAULT_ORDER_TYPE_VALUES.includes(option.value),
            ) || [];

          // If current value is not allowed, set to first allowed value
          if (
            filteredValue &&
            !ALLOWED_DEFAULT_ORDER_TYPE_VALUES.includes(filteredValue)
          ) {
            filteredValue = ALLOWED_DEFAULT_ORDER_TYPE_VALUES[0];
          }
        } else if (config.key === "order_types_allowed") {
          // Filter options to only show allowed order types
          filteredOptions =
            config.options?.filter((option) =>
              ALLOWED_ORDER_TYPES_ALLOWED_VALUES.includes(option.value),
            ) || [];

          // Filter the current value (pipe-separated string) to only include allowed IDs
          if (filteredValue && typeof filteredValue === "string") {
            const valueArray = filteredValue.split("|");
            const filteredArray = valueArray.filter((id) =>
              ALLOWED_ORDER_TYPES_ALLOWED_VALUES.includes(id),
            );
            filteredValue = filteredArray.join("|");
          }
        }

        return {
          ...config,
          value: filteredValue,
          options: filteredOptions,
        };
      }) || [];

  const orderTypesCategory = basicOrderTypesCategory
    ? {
        ...basicOrderTypesCategory,
        configs: processedOrderTypesConfigs,
      }
    : null;

  // Combine all filtered categories with the order types category
  const allFilteredCategories = orderTypesCategory
    ? [...filteredCategories, orderTypesCategory]
    : filteredCategories;

  // Intelligent loading logic
  const isLoading =
    basicCategoryLoading ||
    (basicCategory && platformCategoriesLoading) ||
    (basicPlatformCategory && platformSettingsLoading);

  // Check if we have all required data for the next step
  const hasBasicCategory = !!basicCategory;
  const hasPlatformCategories = !!platformCategories;
  const hasBasicPlatformCategory = !!basicPlatformCategory;
  const hasPlatformSettings = !!platformSettingsCategories;

  // Determine if we should show error or no data
  const shouldShowError =
    basicCategoryError || platformCategoriesError || platformSettingsError;
  const shouldShowNoData =
    !isLoading &&
    hasBasicCategory &&
    hasPlatformCategories &&
    !hasBasicPlatformCategory;
  const shouldShowNoSettings =
    !isLoading &&
    hasBasicCategory &&
    hasPlatformCategories &&
    hasBasicPlatformCategory &&
    !hasPlatformSettings;

  return {
    data: allFilteredCategories,
    isLoading,
    error: shouldShowError,
    shouldShowNoData,
    shouldShowNoSettings,
    // Individual loading states for debugging
    loadingStates: {
      basicCategoryLoading,
      platformCategoriesLoading,
      platformSettingsLoading,
    },
  };
};

/**
 * Hook to get pro config category (key_pro)
 * @returns {Object} Query result with pro category data
 */
export const useProConfigCategory = () => {
  const queryParams = {
    where: [{ attribute: "key", value: "key_pro" }],
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["config-categories", "pro"],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result?.[0] || null;
    },
    enabled: isEndpointEnabled("config-categories", "list"),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to get pro preorder settings
 * @returns {Object} Query result with pro preorder category
 */
export const useProPreorderSettings = () => {
  const {
    data: proCategory,
    isLoading: proCategoryLoading,
    error: proCategoryError,
  } = useProConfigCategory();

  const {
    data: proCategories,
    isLoading: proCategoriesLoading,
    error: proCategoriesError,
  } = useConfigCategories(proCategory?.id);

  const proPreorderCategory = proCategories?.find(
    (category) => category.key === "pro_preorder",
  );

  // Filter configs to only show preorder_status_enabled
  const processedPreorderConfigs = proPreorderCategory?.configs || [];

  const preorderCategory = proPreorderCategory
    ? {
        ...proPreorderCategory,
        configs: processedPreorderConfigs,
      }
    : null;

  const isLoading = proCategoryLoading || (proCategory && proCategoriesLoading);

  return {
    data: preorderCategory ? [preorderCategory] : [],
    isLoading,
    error: proCategoryError || proCategoriesError,
  };
};

/**
 * Hook to get configs for a specific category
 * @param {number} categoryId - Category ID
 * @returns {Object} Query result with configs data
 */
export const useCategoryConfigs = (categoryId) => {
  const queryParams = {
    orderBy: "rank",
    where: [{ attribute: "parent_category_id", value: categoryId }],
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["config-categories", "configs", categoryId],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || [];
    },
    enabled: isEndpointEnabled("config-categories", "list") && !!categoryId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to update a config value
 * @returns {Object} Mutation result
 */
export const useUpdateConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, key, value }) => {
      const response = await apiClient.put(`/configs/${configId}`, {
        key,
        value,
      });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["config-categories"] });
      queryClient.invalidateQueries({ queryKey: ["global-configs"] });
    },
  });
};

/**
 * Hook to update an image-type config value. Sends the image as a base64 data
 * URI in the `image` field; the backend uploads it and stores the URL.
 * @returns {Object} Mutation result
 */
export const useUpdateConfigImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, image }) => {
      const response = await apiClient.put(`/configs/${configId}`, { image });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["config-categories"] });
      queryClient.invalidateQueries({ queryKey: ["global-configs"] });
    },
  });
};

/**
 * Hook to get the dashboard_theme category configs (white-label branding).
 * @returns {Object} Query result; `data` is the array of configs.
 */
export const useDashboardThemeConfigs = () => {
  const queryString = buildQueryParams({
    where: [{ attribute: "key", value: "dashboard_theme" }],
  });

  return useQuery({
    queryKey: ["config-categories", "dashboard_theme"],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result?.[0]?.configs || [];
    },
    enabled: isEndpointEnabled("config-categories", "list"),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

export default {
  useBasicConfigCategory,
  useConfigCategories,
  useBasicPlatformCategory,
  usePlatformSettingsCategories,
  useProConfigCategory,
  useProPreorderSettings,
  useCategoryConfigs,
  useUpdateConfig,
  useUpdateConfigImage,
  useDashboardThemeConfigs,
};
